import { useState, useEffect } from 'react';
import { tumansiApi } from '../../services/tumansiApi';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, X, Save, Wallet, User } from 'lucide-react';

const PAYMENT_METHODS = [
    { value: 'cash', label: 'Cash' },
    { value: 'mpesa', label: 'M-Pesa' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'cheque', label: 'Cheque' },
];

const inp = { width: '100%', padding: '10px 12px', borderRadius: 9, border: '1.5px solid #e2e8f0', fontSize: '0.9rem', fontFamily: 'Poppins, sans-serif', outline: 'none', boxSizing: 'border-box' };

function Field({ label, children }) {
    return (
        <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>
                {label}
            </label>
            {children}
        </div>
    );
}

function Modal({ title, onClose, children }) {
    return (
        <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)',
            zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
        }}>
            <div style={{
                background: '#fff', borderRadius: 18, width: '100%', maxWidth: 600,
                boxShadow: '0 20px 60px rgba(0,0,0,.2)', overflow: 'hidden',
            }}>
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '1.25rem 1.5rem', borderBottom: '1px solid #e2e8f0',
                }}>
                    <h3 style={{ margin: 0, fontWeight: 800, fontSize: '1.05rem', color: '#1e293b' }}>{title}</h3>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', display: 'flex' }}>
                        <X size={20} />
                    </button>
                </div>
                <div style={{ padding: '1.5rem', maxHeight: '70vh', overflowY: 'auto' }}>{children}</div>
            </div>
        </div>
    );
}

export default function RiderPayments() {
    const [payments, setPayments] = useState([]);
    const [riders, setRiders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null); // null | 'create' | { id, ...payment }
    const [saving, setSaving] = useState(false);
    const [riderFilter, setRiderFilter] = useState('all');

    const [form, setForm] = useState({
        rider_id: '',
        payment_date: new Date().toISOString().split('T')[0],
        payment_period_start: '',
        payment_period_end: '',
        deliveries_completed: '',
        total_earnings_kes: '',
        deductions_kes: '0.00',
        net_payment_kes: '',
        payment_method: 'mpesa',
        payment_reference: '',
        notes: ''
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [paymentsRes, ridersRes] = await Promise.all([
                tumansiApi.adminRiderPayments({ limit: 200 }),
                tumansiApi.adminRiders({})
            ]);
            setPayments(paymentsRes.data);
            setRiders(ridersRes.data);
        } catch {
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const openCreateModal = () => {
        setForm({
            rider_id: '',
            payment_date: new Date().toISOString().split('T')[0],
            payment_period_start: '',
            payment_period_end: '',
            deliveries_completed: '',
            total_earnings_kes: '',
            deductions_kes: '0.00',
            net_payment_kes: '',
            payment_method: 'mpesa',
            payment_reference: '',
            notes: ''
        });
        setModal('create');
    };

    const openEditModal = (payment) => {
        setForm({
            rider_id: payment.rider_id,
            payment_date: payment.payment_date.split('T')[0],
            payment_period_start: payment.payment_period_start.split('T')[0],
            payment_period_end: payment.payment_period_end.split('T')[0],
            deliveries_completed: payment.deliveries_completed.toString(),
            total_earnings_kes: payment.total_earnings_kes.toString(),
            deductions_kes: payment.deductions_kes.toString(),
            net_payment_kes: payment.net_payment_kes.toString(),
            payment_method: payment.payment_method,
            payment_reference: payment.payment_reference || '',
            notes: payment.notes || ''
        });
        setModal(payment);
    };

    const calculateNet = () => {
        const total = parseFloat(form.total_earnings_kes) || 0;
        const deductions = parseFloat(form.deductions_kes) || 0;
        const net = total - deductions;
        setForm({ ...form, net_payment_kes: net.toFixed(2) });
    };

    useEffect(() => {
        if (form.total_earnings_kes || form.deductions_kes) {
            calculateNet();
        }
    }, [form.total_earnings_kes, form.deductions_kes]);

    const handleSave = async () => {
        if (!form.rider_id || !form.payment_date || !form.payment_period_start || !form.payment_period_end || !form.net_payment_kes) {
            toast.error('Please fill in all required fields');
            return;
        }

        setSaving(true);
        try {
            const payload = {
                rider_id: form.rider_id,
                payment_date: new Date(form.payment_date).toISOString(),
                payment_period_start: new Date(form.payment_period_start).toISOString(),
                payment_period_end: new Date(form.payment_period_end).toISOString(),
                deliveries_completed: parseInt(form.deliveries_completed) || 0,
                total_earnings_kes: parseFloat(form.total_earnings_kes) || 0,
                deductions_kes: parseFloat(form.deductions_kes) || 0,
                net_payment_kes: parseFloat(form.net_payment_kes),
                payment_method: form.payment_method,
                payment_reference: form.payment_reference || null,
                notes: form.notes || null
            };

            if (modal === 'create') {
                const res = await tumansiApi.adminCreateRiderPayment(payload);
                setPayments(prev => [res.data, ...prev]);
                toast.success('Payment recorded successfully');
            } else {
                const res = await tumansiApi.adminUpdateRiderPayment(modal.id, payload);
                setPayments(prev => prev.map(p => p.id === modal.id ? res.data : p));
                toast.success('Payment updated successfully');
            }
            setModal(null);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to save payment');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!confirm('Are you sure you want to delete this payment record?')) return;

        try {
            await tumansiApi.adminDeleteRiderPayment(id);
            setPayments(prev => prev.filter(p => p.id !== id));
            toast.success('Payment record deleted');
        } catch {
            toast.error('Failed to delete payment');
        }
    };

    const filteredPayments = riderFilter === 'all'
        ? payments
        : payments.filter(p => p.rider_id === riderFilter);

    const totalPaid = filteredPayments.reduce((sum, p) => sum + parseFloat(p.net_payment_kes), 0);

    const S = {
        btn: (color) => ({
            display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px',
            background: color, color: '#fff', border: 'none', borderRadius: 7, cursor: 'pointer',
            fontSize: '0.8rem', fontWeight: 600, fontFamily: 'Poppins, sans-serif',
            whiteSpace: 'nowrap'
        }),
        th: { padding: '12px 10px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' },
        td: { padding: '14px 10px', fontSize: '0.85rem', color: '#334155', borderBottom: '1px solid #f1f5f9' },
    };

    if (loading) {
        return <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Loading payments...</div>;
    }

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.25rem' }}>
                <div>
                    <h1 style={{ fontWeight: 800, fontSize: '1.4rem', color: '#1e293b', margin: 0 }}>💳 Rider Payments</h1>
                    <p style={{ color: '#64748b', fontSize: '0.85rem', margin: '4px 0 0' }}>
                        Total Paid: <strong style={{ color: '#059669' }}>KES {totalPaid.toLocaleString()}</strong> ({filteredPayments.length} payments)
                    </p>
                </div>
                <button onClick={openCreateModal} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '10px 18px', background: '#059669', color: '#fff', borderRadius: 10, border: 'none', fontWeight: 700, cursor: 'pointer', fontSize: '0.88rem', fontFamily: 'Poppins, sans-serif' }}>
                    <Plus size={15} /> Record Payment
                </button>
            </div>

            {/* Rider Filter */}
            <div style={{ marginBottom: '1.5rem' }}>
                <select value={riderFilter} onChange={e => setRiderFilter(e.target.value)} style={{ ...inp, maxWidth: 300 }}>
                    <option value="all">All Riders</option>
                    {riders.map(r => (
                        <option key={r.id} value={r.id}>{r.full_name} - {r.phone}</option>
                    ))}
                </select>
            </div>

            {/* Mobile Card View */}
            <div className="mobile-only" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {filteredPayments.length === 0 ? (
                    <div style={{ background: '#fff', borderRadius: 14, padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>
                        No payment records found
                    </div>
                ) : (
                    filteredPayments.map(payment => (
                        <div key={payment.id} style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '1rem', boxShadow: '0 1px 4px rgba(0,0,0,.05)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.75rem' }}>
                                <div>
                                    <div style={{ fontWeight: 800, color: '#1e293b', fontSize: '1rem' }}>{payment.rider_name}</div>
                                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 2 }}>
                                        {new Date(payment.payment_date).toLocaleDateString()}
                                    </div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <span style={{ background: '#f1f5f9', padding: '4px 10px', borderRadius: 6, fontSize: '0.75rem', fontWeight: 600 }}>
                                        {PAYMENT_METHODS.find(m => m.value === payment.payment_method)?.label || payment.payment_method}
                                    </span>
                                </div>
                            </div>
                            <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '0.75rem', fontSize: '0.85rem' }}>
                                <div style={{ marginBottom: '0.5rem' }}>
                                    <span style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 600 }}>Payment Period:</span>
                                    <div style={{ color: '#334155', fontSize: '0.8rem' }}>
                                        {new Date(payment.payment_period_start).toLocaleDateString()} - {new Date(payment.payment_period_end).toLocaleDateString()}
                                    </div>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #f1f5f9' }}>
                                    <div>
                                        <span style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 600 }}>Deliveries:</span>
                                        <div style={{ fontWeight: 700, color: '#0f62a8' }}>{payment.deliveries_completed}</div>
                                    </div>
                                    <div>
                                        <span style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 600 }}>Earnings:</span>
                                        <div style={{ fontWeight: 700, color: '#334155' }}>KES {parseFloat(payment.total_earnings_kes).toLocaleString()}</div>
                                    </div>
                                </div>
                                {payment.deductions_kes > 0 && (
                                    <div style={{ marginTop: '0.5rem' }}>
                                        <span style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 600 }}>Deductions:</span>
                                        <div style={{ fontWeight: 700, color: '#dc2626' }}>-KES {parseFloat(payment.deductions_kes).toLocaleString()}</div>
                                    </div>
                                )}
                                <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #f1f5f9' }}>
                                    <span style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 600 }}>Net Payment:</span>
                                    <div style={{ fontWeight: 800, color: '#059669', fontSize: '1.2rem' }}>KES {parseFloat(payment.net_payment_kes).toLocaleString()}</div>
                                </div>
                                {payment.notes && (
                                    <div style={{ marginTop: '0.5rem' }}>
                                        <span style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 600 }}>Notes:</span>
                                        <div style={{ color: '#334155', fontSize: '0.8rem', fontStyle: 'italic' }}>{payment.notes}</div>
                                    </div>
                                )}
                                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                                    <button style={{ ...S.btn('#0f62a8'), flex: 1 }} onClick={() => openEditModal(payment)}>
                                        <Edit2 size={12} /> Edit
                                    </button>
                                    <button style={{ ...S.btn('#dc2626'), flex: 1 }} onClick={() => handleDelete(payment.id)}>
                                        <Trash2 size={12} /> Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Desktop Table View */}
            <div className="desktop-only" style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th style={S.th}>Rider</th>
                                <th style={S.th}>Payment Date</th>
                                <th style={S.th}>Period</th>
                                <th style={S.th}>Deliveries</th>
                                <th style={S.th}>Earnings</th>
                                <th style={S.th}>Deductions</th>
                                <th style={S.th}>Net Payment</th>
                                <th style={S.th}>Method</th>
                                <th style={S.th}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredPayments.length === 0 ? (
                                <tr>
                                    <td colSpan="9" style={{ ...S.td, textAlign: 'center', color: '#94a3b8', padding: '2rem' }}>
                                        No payment records found
                                    </td>
                                </tr>
                            ) : (
                                filteredPayments.map(payment => (
                                    <tr key={payment.id}>
                                        <td style={S.td}>
                                            <strong>{payment.rider_name}</strong>
                                        </td>
                                        <td style={S.td}>{new Date(payment.payment_date).toLocaleDateString()}</td>
                                        <td style={S.td}>
                                            {new Date(payment.payment_period_start).toLocaleDateString()} - {new Date(payment.payment_period_end).toLocaleDateString()}
                                        </td>
                                        <td style={S.td}>{payment.deliveries_completed}</td>
                                        <td style={S.td}>KES {parseFloat(payment.total_earnings_kes).toLocaleString()}</td>
                                        <td style={S.td}>
                                            {payment.deductions_kes > 0 ? (
                                                <span style={{ color: '#dc2626' }}>-KES {parseFloat(payment.deductions_kes).toLocaleString()}</span>
                                            ) : '-'}
                                        </td>
                                        <td style={S.td}>
                                            <strong style={{ color: '#059669' }}>KES {parseFloat(payment.net_payment_kes).toLocaleString()}</strong>
                                        </td>
                                        <td style={S.td}>
                                            <span style={{ background: '#f1f5f9', padding: '4px 10px', borderRadius: 6, fontSize: '0.8rem', fontWeight: 600 }}>
                                                {PAYMENT_METHODS.find(m => m.value === payment.payment_method)?.label || payment.payment_method}
                                            </span>
                                        </td>
                                        <td style={S.td}>
                                            <div style={{ display: 'flex', gap: 5 }}>
                                                <button style={S.btn('#0f62a8')} onClick={() => openEditModal(payment)}>
                                                    <Edit2 size={12} /> Edit
                                                </button>
                                                <button style={S.btn('#dc2626')} onClick={() => handleDelete(payment.id)}>
                                                    <Trash2 size={12} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Create/Edit Modal */}
            {modal && (
                <Modal title={modal === 'create' ? '➕ Record New Payment' : '✏️ Edit Payment'} onClose={() => setModal(null)}>
                    <Field label="Rider *">
                        <select style={inp} value={form.rider_id} onChange={e => setForm({ ...form, rider_id: e.target.value })} disabled={modal !== 'create'}>
                            <option value="">-- Select Rider --</option>
                            {riders.map(r => (
                                <option key={r.id} value={r.id}>{r.full_name} - {r.phone}</option>
                            ))}
                        </select>
                    </Field>

                    <Field label="Payment Date *">
                        <input type="date" style={inp} value={form.payment_date} onChange={e => setForm({ ...form, payment_date: e.target.value })} />
                    </Field>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <Field label="Period Start *">
                            <input type="date" style={inp} value={form.payment_period_start} onChange={e => setForm({ ...form, payment_period_start: e.target.value })} />
                        </Field>
                        <Field label="Period End *">
                            <input type="date" style={inp} value={form.payment_period_end} onChange={e => setForm({ ...form, payment_period_end: e.target.value })} />
                        </Field>
                    </div>

                    <Field label="Deliveries Completed">
                        <input type="number" style={inp} value={form.deliveries_completed} onChange={e => setForm({ ...form, deliveries_completed: e.target.value })} placeholder="0" />
                    </Field>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <Field label="Total Earnings (KES)">
                            <input type="number" step="0.01" style={inp} value={form.total_earnings_kes} onChange={e => setForm({ ...form, total_earnings_kes: e.target.value })} placeholder="0.00" />
                        </Field>
                        <Field label="Deductions (KES)">
                            <input type="number" step="0.01" style={inp} value={form.deductions_kes} onChange={e => setForm({ ...form, deductions_kes: e.target.value })} placeholder="0.00" />
                        </Field>
                    </div>

                    <Field label="Net Payment (KES) *">
                        <input type="number" step="0.01" style={{...inp, background: '#f8fafc', fontWeight: 700}} value={form.net_payment_kes} onChange={e => setForm({ ...form, net_payment_kes: e.target.value })} placeholder="Calculated automatically" />
                    </Field>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <Field label="Payment Method *">
                            <select style={inp} value={form.payment_method} onChange={e => setForm({ ...form, payment_method: e.target.value })}>
                                {PAYMENT_METHODS.map(m => (
                                    <option key={m.value} value={m.value}>{m.label}</option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Reference / Transaction ID">
                            <input style={inp} value={form.payment_reference} onChange={e => setForm({ ...form, payment_reference: e.target.value })} placeholder="e.g. MPESA123" />
                        </Field>
                    </div>

                    <Field label="Notes">
                        <textarea style={{ ...inp, minHeight: 60, resize: 'vertical' }} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Any additional notes..." />
                    </Field>

                    <button disabled={saving} onClick={handleSave} style={{ width: '100%', padding: '12px', background: '#059669', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer', fontFamily: 'Poppins, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 8 }}>
                        <Save size={15} /> {saving ? 'Saving...' : modal === 'create' ? 'Record Payment' : 'Update Payment'}
                    </button>
                </Modal>
            )}
        </div>
    );
}
